import { API_ENDPOINTS } from "@/constants";
import { isPremium } from "@/helpers/helpers";
import { getApiService } from "@/services/apiService";
import { ApiResponse, UserData } from "@/types";
import { BadgeColor } from "@/types/domain/auction";
import { BillingPeriod, Price } from "@/types/domain/subscription";
import { CheckCircleIcon, CreditCardIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { Coins, Crown, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Badge from "./Badge";
import { reportAppError } from '@/helpers/sentry';

interface PricingSectionProps {
    showUserInfo?: boolean;
    userData?: UserData;
    onSubscribe?: (priceId: string) => void;
    onChangeSubscription?: (priceId: string) => void;
    isLoading?: boolean;
    loadingPriceId?: string | null;
    changingSubscription?: boolean;
    onCancelDialog?: () => void;
    onAccessTools?: () => void;
    onResumeSubscription?: () => void;
    onBillingPortal?: () => void;
    billingPortalLoading?: boolean;
}

const PricingSection: React.FC<PricingSectionProps> = ({
    showUserInfo = false,
    userData,
    onSubscribe,
    onChangeSubscription,
    isLoading = false,
    loadingPriceId = null,
    changingSubscription = false,
    onCancelDialog,
    onAccessTools,
    onResumeSubscription,
    onBillingPortal,
    billingPortalLoading = false,
}) => {
    const { t } = useTranslation(['subscription', 'errors']);
    const api = getApiService();
    const navigate = useNavigate();

    const translatedBasicFeatures = [
        t('subscription:features.basic.0'),
        t('subscription:features.basic.1'),
        t('subscription:features.basic.2'),
        t('subscription:features.basic.3'),
        t('subscription:features.basic.4'),
        t('subscription:features.basic.5'),
        t('subscription:features.basic.6'),
        t('subscription:features.basic.7'),
        t('subscription:features.basic.8'),
    ];

    const translatedPremiumFeatures = [
        t('subscription:features.premium.0'),
        t('subscription:features.premium.1'),
        t('subscription:features.premium.2'),
        t('subscription:features.premium.3'),
        t('subscription:features.premium.4'),
        t('subscription:features.premium.5'),
        t('subscription:features.premium.6'),
        t('subscription:features.premium.7'),
        t('subscription:features.premium.8'),
        t('subscription:features.premium.9'),
        t('subscription:features.premium.10'),
        t('subscription:features.premium.11'),
    ];

    const [prices, setPrices] = useState<Price[]>([]);
    const [bestPrice, setBestPrice] = useState<number>();
    const [currency, setCurrency] = useState<string>();
    const [savings, setSavings] = useState<number>();
    const [activeBillingPeriod, setActiveBillingPeriod] = useState<BillingPeriod>('yearly');
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({});
    const FEATURE_PREVIEW_COUNT = 6;

    const loadPrices = useCallback(async (): Promise<void> => {
        try {
            const response = await api.get(API_ENDPOINTS.PAYMENTS.PRICES);

            if (!response.ok) {
                throw new Error(`Failed to load prices: ${response.statusText}`);
            }

            const data: ApiResponse<Price[]> = await response.json();
            setPrices(data.data.filter((item) => item.active || item.id === userData?.stripeSubscription?.items.data[0].price.id));
            setDataLoaded(true);
        } catch (error) {
            reportAppError("Error loading prices", error);
            if (showUserInfo) {
                toast.error(t('errors:subscription.failedLoadPricing'));
            }
        }
    }, [api, showUserInfo, userData?.stripeSubscription?.items.data, t]);

    // Helper function to get plan type from lookup_key or nickname.
    // Defined before useCallbacks below that close over it.
    const getPlanType = (price: Price): string => {
        const key = price.lookup_key || '';
        if (key.toLowerCase().includes('premium')) return 'Premium';
        if (key.toLowerCase().includes('basic')) return 'Basic';
        return 'Basic';
    };

    // Helper function to get current user's subscription details
    const getCurrentSubscriptionInfo = useCallback(() => {
        if (!showUserInfo) {
            return null;
        }

        const currentPrice = prices.find(p => p.id === userData?.stripeSubscription?.items.data[0].price.id);
        if (!currentPrice) return null;

        return {
            priceId: currentPrice.id,
            planType: getPlanType(currentPrice),
            interval: currentPrice.recurring.interval,
            lookupKey: currentPrice.lookup_key
        };
    }, [prices, showUserInfo, userData?.stripeSubscription?.items.data]);

    // Calculate savings for yearly plans
    const calculateYearlySavings = useCallback((yearlyPrice: Price): number => {
        const planType = getPlanType(yearlyPrice);
        const monthlyEquivalent = prices.find(
            p => p.recurring.interval === 'month' && getPlanType(p) === planType
        );

        if (!monthlyEquivalent) return 0;

        const yearlyMonthlyPrice = yearlyPrice.unit_amount / 12;
        const monthlySavings = monthlyEquivalent.unit_amount - yearlyMonthlyPrice;
        return Math.round((monthlySavings / monthlyEquivalent.unit_amount) * 100);
    }, [prices]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate fetch-on-mount
        loadPrices();
    }, [loadPrices]);

    useEffect(() => {
        if (prices.length === 0) return;

        if (!showUserInfo || prices.filter(item => item.recurring.interval === "year").length <= 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- deriving UI state from async-loaded prices
            setActiveBillingPeriod("monthly");
        }

        const currentSub = getCurrentSubscriptionInfo();
        if (currentSub) {
            setActiveBillingPeriod((currentSub.interval === "year") ? "yearly" : "monthly");
        }

        const bPrice = prices.filter((item) => item.active).sort((a, b) => a.unit_amount - b.unit_amount)[0];

        if (bPrice) {
            setBestPrice(bPrice.unit_amount / 100);
            setCurrency(bPrice.currency.toUpperCase());
        }

        const yearlySavings = prices.filter((item) => item.active)
            .filter((item) => item.recurring.interval === "year")
            .map(price => calculateYearlySavings(price))
            .sort((a, b) => a - b);

        if (yearlySavings.length > 0) {
            setSavings(yearlySavings[0]);
        }
    }, [calculateYearlySavings, getCurrentSubscriptionInfo, prices, showUserInfo]);

    // Helper function to format price
    const formatPrice = (unitAmount: number, currency: string): string => {
        const amount = unitAmount / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount);
    };

    // Helper function to determine subscription action for a given price
    const getSubscriptionAction = (price: Price) => {
        const currentSub = getCurrentSubscriptionInfo();
        if (!currentSub) {
            return { action: 'subscribe', label: t('subscription:pricing.subscribeNow') };
        }

        const pricePlanType = getPlanType(price);
        const priceInterval = price.recurring.interval;

        if (currentSub.priceId === price.id) {
            return { action: 'current', label: t('subscription:pricing.currentPlan') };
        }

        if (currentSub.planType === pricePlanType && currentSub.interval !== priceInterval) {
            return { action: 'change', label: priceInterval === 'month' ? t('subscription:pricing.switchToMonthly') : t('subscription:pricing.switchToYearly') };
        }

        if (currentSub.planType !== pricePlanType && currentSub.interval === priceInterval) {
            if (currentSub.planType === 'Basic' && pricePlanType === 'Premium') {
                return { action: 'upgrade', label: t('subscription:pricing.upgradeToPremium') };
            } else if (currentSub.planType === 'Premium' && pricePlanType === 'Basic') {
                return { action: 'downgrade', label: t('subscription:pricing.downgradeToBasic') };
            }
        }

        if (currentSub.planType !== pricePlanType && currentSub.interval !== priceInterval) {
            const billingChange = priceInterval === 'month' ? t('subscription:pricing.monthly').toLowerCase() : t('subscription:pricing.yearly').toLowerCase();
            if (currentSub.planType === 'Basic' && pricePlanType === 'Premium') {
                return { action: 'upgrade', label: t('subscription:pricing.upgradeLabel', { billing: billingChange }) };
            } else if (currentSub.planType === 'Premium' && pricePlanType === 'Basic') {
                return { action: 'downgrade', label: t('subscription:pricing.downgradeLabel', { billing: billingChange }) };
            }
        }

        return { action: 'change', label: t('subscription:pricing.switchPlan') };
    };

    // Filter prices by billing period
    const getFilteredPrices = (period: BillingPeriod): Price[] => {
        const targetInterval = period === 'monthly' ? 'month' : 'year';
        return prices
            .filter(price => price.recurring.interval === targetInterval)
            .sort((a, _) => {
                const aType = getPlanType(a);
                return aType === 'Basic' ? -1 : 1;
            });
    };

    const hasYearlyPrices = prices.filter(item => item.recurring.interval === "year").length > 0;
    const filteredPrices = getFilteredPrices(activeBillingPeriod);

    // Show loading state until data is loaded
    if (!dataLoaded) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="text-gray-500">{t('subscription:pricing.loadingInfo')}</div>
            </div>
        );
    }

    // If no prices available, show fallback
    if (prices.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="text-gray-500">{t('subscription:pricing.noPriceInfo')}</div>
            </div>
        );
    }

    return (
        <div className="flex items-center flex-col">
            {/* Billing Period Tabs - only show if there are yearly prices */}
            {showUserInfo && (
                <div className="text-center pt-10 px-4">
                    {userData?.goldSubscription ? (
                        <>
                            <h3 className="font-display text-4xl sm:text-5xl font-medium bg-linear-to-r from-amber-200 via-yellow-200 to-amber-300 bg-clip-text text-transparent">
                                {t('subscription:pricing.goldSubHeading')}
                            </h3>
                            <p className="mt-3 text-base text-zinc-400">
                                {t('subscription:pricing.goldSubHeadingSubtitle')}
                            </p>
                        </>
                    ) : (
                        <>
                            <h3 className="font-display text-5xl font-medium text-white">
                                {t('subscription:pricing.takeGoldmakingNextLevel')}
                            </h3>
                            <p className="mt-2 text-base text-white">
                                {t('subscription:pricing.subscribeFrom', { price: bestPrice, currency })}
                            </p>
                        </>
                    )}
                </div>
            )}
            {userData?.goldSubscription ? (
                <div className="mt-8 mb-8 w-full max-w-2xl px-4">
                    <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-linear-to-br from-amber-950/50 via-yellow-950/30 to-zinc-900/60 p-6 shadow-lg shadow-amber-500/5">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
                        <div className="relative flex items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/15">
                                <Coins className="h-6 w-6 text-amber-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-semibold text-amber-300">
                                        {t('subscription:pricing.goldSubTitle')}
                                    </p>
                                    <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                                        {userData.goldSubscription.type}
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-zinc-300">
                                    {t('subscription:pricing.goldSubActive', {
                                        type: userData.goldSubscription.type,
                                        expiration: new Date(userData.goldSubscription.expiration).toLocaleString()
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>

                    {!isPremium(userData?.accessType) && (
                        <div className="mt-4 rounded-xl border border-indigo-500/20 bg-linear-to-br from-indigo-950/30 to-zinc-900/40 p-5">
                            <div className="flex items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-indigo-500/30 bg-indigo-500/10">
                                    <Crown className="h-4.5 w-4.5 text-indigo-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-indigo-300">
                                        {t('subscription:pricing.goldSubUpgradeTitle')}
                                    </p>
                                    <p className="mt-1 text-sm text-zinc-300">
                                        {t('subscription:pricing.upgradeToPremiumContact')}{" "}
                                        <a
                                            href="https://discord.gg/aprWSYE5JP"
                                            className="inline-flex items-center gap-1 font-medium text-indigo-400 transition-colors hover:text-indigo-300"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <MessageCircle className="h-3.5 w-3.5" />
                                            {t('subscription:pricing.discordName')}
                                        </a>.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {/* Billing Portal Button - show when user has a subscription */}
                    {showUserInfo && userData?.hasAccess && userData?.stripeSubscription?.customer && onBillingPortal && (
                        <div className="mt-6 mb-4">
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-fp-border hover:bg-gray-600 text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={onBillingPortal}
                                disabled={billingPortalLoading}
                            >
                                <CreditCardIcon className="size-5" />
                                {billingPortalLoading ? t('subscription:pricing.manageBillingOpening') : t('subscription:pricing.manageBilling')}
                            </button>
                        </div>
                    )}

                    {hasYearlyPrices && (
                        <>
                            <div className="mt-6 flex justify-center">
                                <div className="inline-flex rounded-sm border border-white/10 bg-white/[0.02] p-1">
                                    <button
                                        type="button"
                                        className={clsx(
                                            "rounded-sm px-4 py-1.5 text-sm font-medium",
                                            activeBillingPeriod === 'monthly'
                                                ? 'bg-white/10 text-white'
                                                : 'text-gray-400 hover:text-white'
                                        )}
                                        onClick={() => setActiveBillingPeriod('monthly')}
                                    >
                                        {t('subscription:pricing.monthly')}
                                    </button>
                                    <button
                                        type="button"
                                        className={clsx(
                                            "rounded-sm px-4 py-1.5 text-sm font-medium",
                                            activeBillingPeriod === 'yearly'
                                                ? 'bg-white/10 text-white'
                                                : 'text-gray-400 hover:text-white'
                                        )}
                                        onClick={() => setActiveBillingPeriod('yearly')}
                                    >
                                        {t('subscription:pricing.yearly')}
                                    </button>
                                </div>
                            </div>
                            {savings && (
                                <p className="mt-3 text-center text-xs text-amber-300">
                                    {t('subscription:pricing.saveUpTo', { percent: Math.abs(savings) })}
                                </p>
                            )}
                        </>
                    )}

                    <div className="mt-6 flow-root">
                        <div className={clsx(
                            "isolate grid gap-4",
                            filteredPrices.length === 1
                                ? "grid-cols-1 max-w-md mx-auto"
                                : "grid-cols-1 lg:grid-cols-2"
                        )}>
                            {filteredPrices.map((price) => {
                                const planType = getPlanType(price);
                                const isPremiumPlan = planType === 'Premium';
                                const features = isPremiumPlan ? translatedPremiumFeatures : translatedBasicFeatures;
                                const subscriptionAction = getSubscriptionAction(price);
                                const isCurrentlyLoading = isLoading && loadingPriceId === price.id;
                                const isHighlighted = (isPremiumPlan || filteredPrices.length === 1) && price.recurring.interval === "year";
                                const isExpanded = expandedFeatures[price.id] ?? false;
                                const visibleFeatures = isExpanded ? features : features.slice(0, FEATURE_PREVIEW_COUNT);
                                const hiddenFeatureCount = features.length - FEATURE_PREVIEW_COUNT;
                                let subStatus = t('subscription:pricing.statusCanceled');
                                let subColor: BadgeColor = "red";
                                if (userData?.isActive) {
                                    subStatus = t('subscription:pricing.statusActive');
                                    subColor = "green";
                                }
                                if (userData?.isExpired) {
                                    subStatus = t('subscription:pricing.statusExpired');
                                    subColor = "red";
                                }
                                if (userData?.inCancelation) {
                                    subStatus = t('subscription:pricing.statusInCancelation');
                                    subColor = "yellow";
                                }

                                return (
                                    <section
                                        key={price.id}
                                        className={clsx(
                                            "relative flex flex-col rounded-sm border bg-white/[0.02] p-6",
                                            isHighlighted ? "border-amber-300/40" : "border-white/10"
                                        )}
                                    >
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-semibold text-white">
                                                {planType}
                                                {subscriptionAction.action === 'current' && (
                                                    <>
                                                        {" "}<Badge
                                                            color={subColor}
                                                            text={subStatus}
                                                        />
                                                    </>
                                                )}
                                            </h3>
                                            {isHighlighted && (
                                                <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-amber-300">
                                                    {t('subscription:pricing.mostPopular')}
                                                </span>
                                            )}
                                        </div>

                                        <p className="mt-4 flex items-baseline gap-x-1">
                                            <span className="text-3xl font-semibold tracking-tight text-white">
                                                {formatPrice(price.unit_amount, price.currency)}
                                            </span>
                                            <span className="text-sm text-gray-400">
                                                {price.recurring.interval === 'year'
                                                    ? t('subscription:pricing.perYear')
                                                    : t('subscription:pricing.perMonth')}
                                            </span>
                                        </p>

                                        {price.recurring.interval === 'year' && (
                                            <p className="mt-1 text-xs text-gray-500">
                                                {formatPrice(Math.round(price.unit_amount / 12), price.currency)}{t('subscription:pricing.perMonth')}
                                            </p>
                                        )}

                                        <ul className="mt-6 space-y-2.5 text-sm text-gray-300">
                                            {visibleFeatures.map((feature) => (
                                                <li key={feature} className="flex gap-3">
                                                    <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-300/80" />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {hiddenFeatureCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setExpandedFeatures((prev) => ({ ...prev, [price.id]: !isExpanded }))}
                                                className="mt-3 self-start text-xs text-gray-400 hover:text-white"
                                            >
                                                {isExpanded
                                                    ? t('subscription:pricing.showFewerFeatures')
                                                    : t('subscription:pricing.showMoreFeatures', { count: hiddenFeatureCount })}
                                            </button>
                                        )}

                                        <div className="mt-6 space-y-2.5">
                                            {showUserInfo && (userData?.hasAccess && userData?.accessType !== "trial") ? (
                                                <>
                                                    {(() => {
                                                        const isCurrentlyLoadingAction = (isLoading || changingSubscription) && loadingPriceId === price.id;

                                                        if (subscriptionAction.action === 'current') {
                                                            const startDate = t('subscription:pricing.startDate', { date: new Date(userData.stripeSubscription!.start_date * 1000).toLocaleString() });
                                                            let nextPaymentDate = t('subscription:pricing.nextPaymentDate', { date: new Date(userData.stripeSubscription!.items.data[0].current_period_end * 1000).toLocaleString() });
                                                            let reason = null;
                                                            if (userData.inCancelation) {
                                                                if (userData.stripeSubscription?.status === "past_due") {
                                                                    reason = t('subscription:pricing.reasonPaymentFailed');
                                                                    nextPaymentDate = t('subscription:pricing.subscriptionEnds', { date: new Date(userData.stripeSubscription!.items.data[0].current_period_end! * 1000).toLocaleString() });
                                                                } else {
                                                                    reason = t('subscription:pricing.reasonCancelled');
                                                                    nextPaymentDate = t('subscription:pricing.subscriptionEnds', { date: new Date(userData.stripeSubscription!.cancel_at! * 1000).toLocaleString() });
                                                                }
                                                            }
                                                            return (
                                                                <>
                                                                    <div className="text-xs text-gray-400">{startDate}</div>
                                                                    <div className="text-xs text-gray-400">{nextPaymentDate}</div>
                                                                    {reason && (
                                                                        <div className="text-xs text-amber-300">{reason}</div>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="w-full rounded-sm border border-white/10 px-5 py-2.5 text-sm font-medium text-gray-400 cursor-default"
                                                                        disabled
                                                                    >
                                                                        {subscriptionAction.label}
                                                                    </button>

                                                                    {onAccessTools && (
                                                                        <button
                                                                            type="button"
                                                                            className="w-full rounded-sm bg-amber-400 px-5 py-2.5 text-sm font-medium text-stone-950 hover:bg-amber-300"
                                                                            onClick={onAccessTools}
                                                                        >
                                                                            {t('subscription:pricing.accessTools')}
                                                                        </button>
                                                                    )}

                                                                    {userData.inCancelation && userData.stripeSubscription?.status !== "past_due" && onResumeSubscription && (
                                                                        <button
                                                                            type="button"
                                                                            className="w-full rounded-sm border border-white/15 px-5 py-2.5 text-sm font-medium text-gray-300 hover:border-white/30 hover:text-white"
                                                                            onClick={onResumeSubscription}
                                                                        >
                                                                            {t('subscription:pricing.resumeSubscription')}
                                                                        </button>
                                                                    )}
                                                                    {!userData.inCancelation && onCancelDialog && (
                                                                        <button
                                                                            type="button"
                                                                            className="w-full rounded-sm border border-white/15 px-5 py-2.5 text-sm font-medium text-gray-400 hover:border-red-400/50 hover:text-red-300"
                                                                            onClick={onCancelDialog}
                                                                        >
                                                                            {t('subscription:pricing.cancelSubscription')}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            );
                                                        }

                                                        // Upgrade is a primary action (amber); downgrade/switch is secondary (white border).
                                                        const isUpgrade = subscriptionAction.action === 'upgrade' || subscriptionAction.action === 'subscribe';
                                                        const buttonClass = isUpgrade
                                                            ? 'bg-amber-400 text-stone-950 hover:bg-amber-300'
                                                            : 'border border-white/15 text-gray-300 hover:border-white/30 hover:text-white';

                                                        return (
                                                            <button
                                                                type="button"
                                                                className={`w-full rounded-sm px-5 py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${buttonClass}`}
                                                                onClick={() => onChangeSubscription?.(price.id)}
                                                                disabled={isCurrentlyLoadingAction}
                                                            >
                                                                {isCurrentlyLoadingAction ? t('subscription:pricing.processing') : subscriptionAction.label}
                                                            </button>
                                                        );
                                                    })()}
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={clsx(
                                                        "w-full rounded-sm px-5 py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed",
                                                        isHighlighted
                                                            ? 'bg-amber-400 text-stone-950 hover:bg-amber-300'
                                                            : 'border border-white/15 text-gray-300 hover:border-white/30 hover:text-white'
                                                    )}
                                                    onClick={() => {
                                                        if (showUserInfo && onSubscribe) {
                                                            onSubscribe(price.id);
                                                        } else {
                                                            navigate("/register");
                                                            window.scrollTo(0, 0);
                                                        }
                                                    }}
                                                    disabled={isCurrentlyLoading}
                                                >
                                                    {isCurrentlyLoading ? t('subscription:pricing.processing') : (showUserInfo ? t('subscription:pricing.subscribeNow') : t('subscription:pricing.getStarted'))}
                                                </button>
                                            )}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PricingSection;
